'use client';

import React, { useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../lib/utils';
import { buttonVariants } from './button-variants';
import { useModalLayer } from './use-modal-layer';

export interface DialogProps {
  role?: 'dialog' | 'alertdialog';
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
  /** Extra classes for the fixed overlay layer, e.g. a higher z-index for session-critical prompts. */
  overlayClassName?: string;
  size?: 'sm' | 'default' | 'wide' | 'md' | 'lg' | 'xl' | 'full';
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
}

// Named utility fallbacks until the foundation exposes 480px / 800px width tokens.
// The default width is already available as the 640px screen-sm utility.
const sizeClasses: Record<string, string> = {
  sm: 'max-w-lg',
  default: 'max-w-screen-sm',
  wide: 'max-w-screen-md',
  md: 'max-w-screen-sm',
  lg: 'max-w-screen-md',
  xl: 'max-w-screen-md',
  full: 'max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)]',
};

export function Dialog({
  overlayClassName,
  role = 'dialog',
  open,
  onClose,
  title,
  description,
  children,
  className,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
}: DialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const dialogId = useId();
  const titleId = `${dialogId}-title`;
  const descId = `${dialogId}-description`;

  useModalLayer({
    open,
    panelRef: dialogRef,
    onClose,
    closeOnEscape,
    focusableSelector: 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  });

  if (!open) return null;

  const content = (
    <div
      // z-[200]: page overlays such as the roster full-screen workspace sit at
      // z-[100]. This dialog portals to document.body, so z-50 painted behind
      // those overlays and hid unassign/cancel/swap from the roster grid.
      className={cn(
        'fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-4',
        overlayClassName,
      )}
      role={role}
      data-modal-layer={role === 'alertdialog' ? '' : undefined}
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={description ? descId : undefined}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-surface-inverse/40 backdrop-blur-sm transition-opacity motion-reduce:transition-none"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Dialog panel — BUG-1: constrain to viewport with a scrollable body
          so tall wizards can't push their footer off-screen on short screens */}
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={cn(
          'relative z-10 flex max-h-[calc(100vh-1.5rem)] w-full min-w-0 flex-col overflow-hidden rounded-overlay bg-surface-sheet shadow-dialog sm:max-h-[calc(100vh-2rem)]',
          'motion-reduce:animate-none animate-in fade-in-0 zoom-in-95 duration-200',
          sizeClasses[size],
          className,
        )}
      >
        {/* Header — fixed, never scrolls */}
        {(title || showCloseButton) && (
          <div className="flex min-w-0 flex-wrap items-start gap-x-4 gap-y-3 shrink-0 border-b border-border px-4 py-4 sm:px-6">
            <div className="min-w-0 flex-[1_1_16rem]">
              {title && (
                <h2
                  id={titleId}
                  className="min-w-0 break-normal [overflow-wrap:break-word] font-body text-card-title text-text-heading"
                >
                  {title}
                </h2>
              )}
              {description && (
                <p id={descId} className="mt-1 break-words font-body text-body-sm text-text-muted">
                  {description}
                </p>
              )}
            </div>
            {showCloseButton && (
              <div className="ml-auto flex min-w-0 flex-[0_1_auto] flex-wrap justify-start gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  data-ui="button"
                  className={buttonVariants({
                    variant: 'ghost',
                    size: 'compact',
                    className: 'w-8 p-0',
                  })}
                  aria-label="Close dialog"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Body — scrollable region; footer stays visible at any viewport height */}
        <div className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-4 sm:px-6">
          {children}
        </div>
      </div>
    </div>
  );

  if (typeof window === 'undefined') return null;
  return createPortal(content, document.body);
}

/* Convenience sub-components for dialog footer.
 * Sticky at the bottom of the scrollable dialog body so tall wizards can
 * never push the primary action out of view (BUG-1). */
export function DialogFooter({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'min-h-control [&_button]:min-h-control sticky bottom-0 z-10 -mx-4 -mb-4 mt-4 flex flex-wrap flex-col items-stretch gap-2 rounded-b-overlay border-t border-border bg-surface-soft px-4 py-4 sm:-mx-6 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:gap-3 sm:px-6',
        className,
      )}
    >
      {children}
    </div>
  );
}
